import { Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { AuthService } from '@auth/services/auth.service'
import { Observable, of } from 'rxjs'

@Injectable({
  providedIn: 'root'
})
export class RoleGuard {
  /**
   * Creates the guard and injects it's dependencies
   * @param router Router
   * @param authService AuthService
   * @param workspaceRole WorkspaceRoleService
   */
  constructor(private router: Router, private authService: AuthService, private workspaceRole: WorkspaceRoleService) {}

  /**
   * Checks if the user has the required role to access the route.
   * Accountants are redirected to organization settings billing for the current workspace.
   * Role is re-synced from the selected workspace on every activation.
   * @param childRoute ActivatedRouteSnapshot
   * @param state RouterStateSnapshot
   * @returns Observable<boolean | UrlTree>
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.canActivateChild(route, state)
  }

  /**
   * Child-route check; same rules as canActivate.
   * Redirects never target a path the navigation is already on, so no role can loop.
   * @param childRoute ActivatedRouteSnapshot
   * @param state RouterStateSnapshot
   */
  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    if (!this.authService.isLoggedIn) return of(this._redirectIfNeeded('/auth/login', state))
    if (this.authService.isAdmin) return of(true)

    this.workspaceRole.syncRoleFromCurrentWorkspace()

    return of(this.makeRoutingDecision(childRoute, state))
  }

  /**
   * Makes the routing decision based on the current user role in the selected workspace.
   * @param route Activated route (reads denyRoles from data when present)
   * @param state Current router state (used to skip no-op redirects)
   * @returns boolean | UrlTree
   */
  private makeRoutingDecision(route: ActivatedRouteSnapshot | null, state?: RouterStateSnapshot | null): boolean | UrlTree {
    if (this.workspaceRole.isAccountant()) {
      return this._redirectIfNeeded(this._getAccountantBillingUrl(), state)
    }

    // Org settings sets denyRoles (viewer/approver); reuse the same access check as the navbar entry.
    if (route?.data?.['denyRoles'] && !this.workspaceRole.canAccessWorkspaceSettings()) {
      return this._redirectIfNeeded('/events/list', state)
    }

    return true
  }

  /**
   * Build the organization-settings billing URL for accountant users.
   * @returns {string} Path to org settings with the billing fragment
   */
  private _getAccountantBillingUrl(): string {
    return this.authService.getOrganizationBillingUrl() ?? '/workspaces/list'
  }

  /**
   * Redirects to targetUrl only when the current navigation is a different path.
   * Query and hash are ignored so fragment-only / query-only differences cannot re-enter the guard.
   * @param targetUrl Destination the guard would otherwise send the user to
   * @param state Current router state
   */
  private _redirectIfNeeded(targetUrl: string, state?: RouterStateSnapshot | null): boolean | UrlTree {
    if (this._isSamePath(state?.url, targetUrl)) {
      return true
    }
    return this.router.parseUrl(targetUrl)
  }

  /**
   * True when both URLs share the same path (trailing slashes, query, and hash ignored).
   * @param currentUrl Router state URL
   * @param targetUrl Intended redirect URL
   */
  private _isSamePath(currentUrl?: string | null, targetUrl?: string | null): boolean {
    if (!currentUrl || !targetUrl) return false
    return this._normalizePath(currentUrl) === this._normalizePath(targetUrl)
  }

  /**
   * Strips query, hash, and trailing slashes for path comparison.
   * @param url Raw URL or router state url
   */
  private _normalizePath(url: string): string {
    const path = url.split('?')[0].split('#')[0].replace(/\/+$/, '')
    return path || '/'
  }
}
